import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { openSMLUpgradePrompt } from '../../../../redux/slices/appInfo/slice';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { useVideoCredits } from './useVideoCredits';
import { isShortOfVideoCredits, videoCreditsLeft } from './videoCreditGate';
import { useIsNativeEmbed } from '../../navigation/hooks/useIsNativeEmbed';
import { creditMessages } from './creditMessages';

export interface VideoCreditGate {
  /** True when the balance (plan plus packs) is below `cost`. False while the credits load. */
  isShort: (cost: number) => boolean;
  /** Opens the credit packs (g8) for the owner, or the owner note (e4) for a member. */
  offerCredits: () => void;
  /**
   * The check before a click that spends credits: true when the call may go. When the balance is
   * below `cost`, it opens the packs at once and answers false (owner rule 2026-10-06).
   */
  canSpend: (cost: number) => boolean;
  /**
   * The text of a button that spends credits while the balance is short: "Buy credits to make it"
   * for the owner on the web, "Not enough video credits" for a member and in a native app's web
   * view (App Store 3.1.1: no purchase words there). The click opens the packs or the owner note.
   */
  shortLabel: string;
  /** "This needs 3 credits. You have 1 left.", for the line under a short button. */
  shortNote: (cost: number) => string;
  /**
   * True for the owner on the web on a paid plan: a short balance offers "Buy credits". Free and
   * the trial get the plan offer instead (packs are for paid plans); a member gets the owner note.
   */
  canBuy: boolean;
}

/**
 * The client side of the video-credit rule. It reads the same credits answer as the credit box
 * (`GET .../credits`), so the pre-click check and the box always agree. The server stays the
 * judge: its 402 `M24_LIMIT_QUOTA` (kind `videoCredit`) opens the same packs through
 * `customBaseQuery`.
 */
export function useVideoCreditGate(accountId: number | null | undefined): VideoCreditGate {
  const dispatch = useDispatch();
  const intl = useIntl();
  const isNativeEmbed = useIsNativeEmbed();
  const { credits } = useVideoCredits({ accountId });
  const isPaidPlan = credits !== null && credits !== undefined && credits.plan !== 'free' && credits.plan !== 'trial';
  const canBuy = (credits?.isOwner ?? false) && !isNativeEmbed && isPaidPlan;
  const shortLabel = intl.formatMessage(canBuy ? creditMessages.gateBuyToMake : creditMessages.gateNotEnough);
  const shortNote = useCallback(
    (cost: number) => intl.formatMessage(creditMessages.gateShortNote, { cost, left: videoCreditsLeft(credits) ?? 0 }),
    [credits, intl]
  );

  const isShort = useCallback((cost: number) => isShortOfVideoCredits(credits, cost), [credits]);

  const offerCredits = useCallback(() => {
    dispatch(
      openSMLUpgradePrompt({
        feature: SMLUpgradeFeature.videoCredits,
        kind: 'videoCredit',
        used: credits?.used ?? null,
        limit: credits?.limit ?? null,
        resetsAt: credits?.resetsAt ?? null,
      })
    );
  }, [credits, dispatch]);

  const canSpend = useCallback(
    (cost: number) => {
      if (!isShortOfVideoCredits(credits, cost)) {
        return true;
      }
      offerCredits();
      return false;
    },
    [credits, offerCredits]
  );

  return { isShort, offerCredits, canSpend, shortLabel, shortNote, canBuy };
}
